import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { NgButtonNavLink } from '@/ui';
import type { LiveVideo } from '@/signalr';
import { checkRecordingExists } from '@ngsos/portal-core/video';
import { getVideoOptions } from '@/lib/video/config';
import { DateFormatter } from '@/features/shared/DateFormatter';
import { incidentRecordingDetailRoute } from './routes';
import { VideoStatsLink } from '@/features/video/VideoStatsLink';

export type VideoRecordingListItemComponentProps = {
  incidentId: string;
  liveVideo: LiveVideo;
};

export function VideoRecordingListItemComponent({ incidentId, liveVideo }: VideoRecordingListItemComponentProps) {
  const { t } = useTranslation();

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const [recordingBlobExists, setRecordingBlobExists] = useState(false);
  const [videoServerUrl, setVideoServerUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const videoOptions = getVideoOptions();
    setVideoServerUrl(videoOptions.VIDEO_SERVER_URL);

    void checkRecordingExists(liveVideo.id, videoOptions.VIDEO_RECORDINGS_ROOT_URL).then((exists) => {
      if (!cancelled) {
        setRecordingBlobExists(exists);
        setIsCheckingAvailability(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [liveVideo.id]);

  return (
    <div className="list-group-item px-0 d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center-center gap-2">
      <div className="d-flex align-items-center justify-content-between flex-grow-1">
        <div className="col-lg-5 py-lg-1">
          {recordingBlobExists ? (
            <NavLink to={incidentRecordingDetailRoute(incidentId, liveVideo.id)}>
              <DateFormatter date={liveVideo.startTimeUtc} formatType="calendar" />
            </NavLink>
          ) : (
            <DateFormatter date={liveVideo.startTimeUtc} formatType="calendar" />
          )}
        </div>
      </div>
      <div className="d-flex justify-content-center gap-1 justify-content-lg-end">
        <VideoStatsLink videoId={liveVideo.id} videoServerUrl={videoServerUrl} className="small align-self-center" />
        {isCheckingAvailability ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        ) : recordingBlobExists ? (
          <NgButtonNavLink
            to={incidentRecordingDetailRoute(incidentId, liveVideo.id)}
            text={t('Button_Play')}
            size="small"
            icon="fal fa-play"
          />
        ) : (
          <span className="badge bg-warning text-dark">{t('Incident_VideoRecordings_NotAvailable')}</span>
        )}
      </div>
    </div>
  );
}
